import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Calendar, Calculator, FileText, Mail, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import AddressAutocomplete from '../components/AddressAutocomplete';

function AddLeadPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    contact_name: '',
    email: '',
    phone: '',
    business_name: '',
    position_title: '',
    interest_level: 'warm',
    source: '',
    assigned_to: '',
    initial_notes: '',
    street_address: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
    add_to_map: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Map form fields to API structure
      const leadData = {
        business_name: formData.business_name,
        contact_name: formData.contact_name,
        email: formData.email,
        phone: formData.phone,
        source: formData.source,
        business_type: formData.position_title, // Using position_title as business context
        current_system: formData.current_system || null,
        monthly_volume: null,
        pain_point: formData.initial_notes || null
      };

      const newLead = await api.createLead(leadData);
      
      toast.success('Lead created successfully!', {
        description: `${formData.business_name} has been added to your pipeline.`
      });
      
      navigate('/leads');
    } catch (error) {
      console.error('Failed to create lead:', error);
      toast.error('Failed to create lead', {
        description: error.message || 'Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    // TODO: Implement draft saving logic
    alert('Draft saved! (Feature to be implemented)');
  };

  const handleQuickAction = (action) => {
    alert(`${action} - Feature to be implemented in Phase 3`);
  };

  const leadSources = ['Referral', 'Website', 'Cold Call', 'Email Campaign', 'Social Media', 'Trade Show', 'Partner', 'Other'];
  const usStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Add New Lead</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Create a new lead or update an existing one in your pipeline
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline"
              onClick={() => alert('Scan Document - Feature to be implemented')}
              data-testid="scan-document-btn"
            >
              <ScanLine className="h-4 w-4 mr-2" />
              Scan Document
              <span className="ml-2 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">3</span>
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/leads')}
            >
              View All Leads
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="Enter full name"
                  required
                  className="mt-1"
                  data-testid="full-name-input"
                />
              </div>

              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                  required
                  className="mt-1"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  required
                  className="mt-1"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <Label>Business Name *</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  placeholder="Enter business name"
                  required
                  className="mt-1"
                  data-testid="business-name-input"
                />
              </div>

              <div className="md:col-span-2">
                <Label>Position/Title</Label>
                <Input
                  value={formData.position_title}
                  onChange={(e) => setFormData({ ...formData, position_title: e.target.value })}
                  placeholder="e.g., Owner, Manager, CFO"
                  className="mt-1"
                  data-testid="position-title-input"
                />
              </div>
            </div>
          </Card>

          {/* Additional Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Additional Information</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Interest Level *</Label>
                <div className="flex items-center space-x-4 mt-2">
                  <label className={`flex-1 flex items-center justify-center space-x-2 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    formData.interest_level === 'hot' ? 'border-orange-500 bg-orange-50' : 'border-border hover:border-orange-300'
                  }`}>
                    <input
                      type="radio"
                      name="interest_level"
                      value="hot"
                      checked={formData.interest_level === 'hot'}
                      onChange={(e) => setFormData({ ...formData, interest_level: e.target.value })}
                      className="w-4 h-4"
                      data-testid="interest-hot"
                    />
                    <span className="text-sm font-medium">🔥 Hot</span>
                  </label>
                  <label className={`flex-1 flex items-center justify-center space-x-2 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    formData.interest_level === 'warm' ? 'border-yellow-500 bg-yellow-50' : 'border-border hover:border-yellow-300'
                  }`}>
                    <input
                      type="radio"
                      name="interest_level"
                      value="warm"
                      checked={formData.interest_level === 'warm'}
                      onChange={(e) => setFormData({ ...formData, interest_level: e.target.value })}
                      className="w-4 h-4"
                      data-testid="interest-warm"
                    />
                    <span className="text-sm font-medium">☀️ Warm</span>
                  </label>
                  <label className={`flex-1 flex items-center justify-center space-x-2 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    formData.interest_level === 'cold' ? 'border-blue-500 bg-blue-50' : 'border-border hover:border-blue-300'
                  }`}>
                    <input
                      type="radio"
                      name="interest_level"
                      value="cold"
                      checked={formData.interest_level === 'cold'}
                      onChange={(e) => setFormData({ ...formData, interest_level: e.target.value })}
                      className="w-4 h-4"
                      data-testid="interest-cold"
                    />
                    <span className="text-sm font-medium">❄️ Cold</span>
                  </label>
                </div>
              </div>

              <div>
                <Label>Lead Source *</Label>
                <Select 
                  value={formData.source}
                  onValueChange={(value) => setFormData({ ...formData, source: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="lead-source-select">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadSources.map((source) => (
                      <SelectItem key={source} value={source.toLowerCase().replace(/ /g, '_')}>
                        {source}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Assign To *</Label>
                <Select 
                  value={formData.assigned_to}
                  onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="assign-to-select">
                    <SelectValue placeholder="Assign to agent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current_user">Current User (Me)</SelectItem>
                    <SelectItem value="demo_agent">Demo Agent</SelectItem>
                    <SelectItem value="sales_team">Sales Team</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label>Initial Notes</Label>
                <Textarea
                  value={formData.initial_notes}
                  onChange={(e) => setFormData({ ...formData, initial_notes: e.target.value })}
                  placeholder="Add any relevant notes about this lead..."
                  rows={3}
                  className="mt-1"
                  data-testid="initial-notes-textarea"
                />
              </div>
            </div>
          </Card>

          {/* Location Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Location Information</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Street Address</Label>
                <AddressAutocomplete
                  value={formData.street_address}
                  onChange={(val) => setFormData(prev => ({ ...prev, street_address: val }))}
                  onPlaceSelected={(place) => {
                    setFormData(prev => ({
                      ...prev,
                      street_address: place.street,
                      city: place.city,
                      state: place.state,
                      zip: place.zip,
                    }));
                  }}
                  placeholder="Start typing an address..."
                  className="mt-1"
                  data-testid="street-address-input"
                />
              </div>

              <div>
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Enter city"
                  className="mt-1"
                  data-testid="city-input"
                />
              </div>

              <div>
                <Label>State</Label>
                <Select 
                  value={formData.state}
                  onValueChange={(value) => setFormData({ ...formData, state: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {usStates.map((state) => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>ZIP Code</Label>
                <Input
                  value={formData.zip}
                  onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  placeholder="Enter ZIP code"
                  className="mt-1"
                  maxLength={10}
                  data-testid="zip-input"
                />
              </div>

              <div>
                <Label>Country</Label>
                <Select 
                  value={formData.country}
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="Mexico">Mexico</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.add_to_map}
                    onChange={(e) => setFormData({ ...formData, add_to_map: e.target.checked })}
                    className="w-4 h-4 rounded border-input"
                    data-testid="add-to-map-checkbox"
                  />
                  <span className="text-sm text-muted-foreground">
                    Add to Map View - Check this box to add this lead to your map view for location-based tracking
                  </span>
                </label>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Take immediate action after creating this lead
            </p>
            <div className="grid gap-4 md:grid-cols-4">
              <button
                type="button"
                onClick={() => handleQuickAction('Schedule Follow-up')}
                className="p-4 border-2 border-dashed rounded-lg hover:border-primary hover:bg-accent transition-colors text-center"
                data-testid="quick-action-schedule"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
                <div className="font-medium text-sm">Schedule Follow-up</div>
              </button>

              <button
                type="button"
                onClick={() => handleQuickAction('Create Pricing')}
                className="p-4 border-2 border-dashed rounded-lg hover:border-primary hover:bg-accent transition-colors text-center"
                data-testid="quick-action-pricing"
              >
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Calculator className="h-6 w-6 text-green-600" />
                </div>
                <div className="font-medium text-sm">Create Pricing</div>
              </button>

              <button
                type="button"
                onClick={() => handleQuickAction('Start Application')}
                className="p-4 border-2 border-dashed rounded-lg hover:border-primary hover:bg-accent transition-colors text-center"
                data-testid="quick-action-application"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
                <div className="font-medium text-sm">Start Application</div>
              </button>

              <button
                type="button"
                onClick={() => handleQuickAction('Send Email')}
                className="p-4 border-2 border-dashed rounded-lg hover:border-primary hover:bg-accent transition-colors text-center"
                data-testid="quick-action-email"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Mail className="h-6 w-6 text-orange-600" />
                </div>
                <div className="font-medium text-sm">Send Email</div>
              </button>
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate('/leads')}
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="outline"
              onClick={handleSaveDraft}
              disabled={loading}
              data-testid="save-draft-btn"
            >
              Save as Draft
            </Button>
            <Button 
              type="submit"
              disabled={loading}
              className="bg-[#3b82f6] hover:bg-[#2563eb]"
              data-testid="save-lead-btn"
            >
              {loading ? 'Saving...' : 'Save Lead'}
            </Button>
          </div>
        </form>
      </div>
  );
}

export default AddLeadPage;
